import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { Tag } from "@/components/ui/Tag";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SECTOR_SLUGS = [
  "consumer-brands-retail",
  "luxury-assets",
  "financial-services",
  "supply-chain-logistics",
  "energy-utilities",
  "ngo-impact",
];

const ROADMAP_SLUG = "energy-utilities";

function Hero() {
  const { t } = useT("solutions");
  return (
    <section
      className="container-frac pt-24 pb-12 md:pt-32 md:pb-16"
      data-testid="solutions-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 max-w-3xl text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function DetailPanel({ slug, sector, labels }) {
  const isRoadmap = slug === ROADMAP_SLUG;
  return (
    <Card
      variant="glass"
      className="scroll-mt-28"
      as="section"
      id={slug}
      data-testid={`solutions-panel-${slug}`}
    >
      <h2 className="text-2xl font-bold leading-snug text-ink-primary sm:text-3xl">
        {sector.label}
      </h2>
      <div className="mt-8 grid gap-8 md:grid-cols-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
            {labels.generates}
          </div>
          <p className="mt-3 text-sm leading-normal text-ink-secondary">
            {sector.generates}
          </p>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
            {labels.automates}
          </div>
          <p className="mt-3 text-sm leading-normal text-ink-secondary">
            {sector.automates}
          </p>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
              {labels.output}
            </span>
            {isRoadmap && <Badge status="ROADMAP" onDark />}
          </div>
          <p className="mt-3 text-sm leading-normal text-ink-secondary">
            {sector.output}
          </p>
        </div>
      </div>
    </Card>
  );
}

export default function Solutions() {
  const location = useLocation();
  const initialSlug =
    (location.hash || "").replace(/^#/, "") || SECTOR_SLUGS[0];
  const [active, setActive] = useState(
    SECTOR_SLUGS.includes(initialSlug) ? initialSlug : SECTOR_SLUGS[0],
  );
  const panelsRef = useRef({});
  const { t } = useT("solutions");
  const locale = useLocale();
  const sectors = t("sectors", { returnObjects: true }) || {};
  const detailLabels = t("detailLabels", { returnObjects: true }) || {};

  const registerPanel = useCallback((slug, el) => {
    if (el) panelsRef.current[slug] = el;
  }, []);

  const selectSector = useCallback((slug) => {
    setActive(slug);
    const nextUrl = `${window.location.pathname}#${slug}`;
    window.history.replaceState(null, "", nextUrl);
    const el = panelsRef.current[slug];
    if (el) {
      const targetY =
        el.getBoundingClientRect().top + window.scrollY - 100;
      const lenis = window.__lenis;
      if (lenis) {
        lenis.scrollTo(targetY, { duration: 1.0 });
      } else {
        window.scrollTo({ top: targetY, behavior: "smooth" });
      }
    }
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const slug = location.hash.replace(/^#/, "");
    if (SECTOR_SLUGS.includes(slug)) {
      setActive(slug);
    }
    const el = panelsRef.current[slug];
    if (el) {
      window.setTimeout(() => {
        const targetY =
          el.getBoundingClientRect().top + window.scrollY - 100;
        const lenis = window.__lenis;
        if (lenis) {
          lenis.scrollTo(targetY, { duration: 1.0 });
        } else {
          window.scrollTo({ top: targetY, behavior: "smooth" });
        }
      }, 350);
    }
  }, [location.hash]);

  return (
    <>
      <PageSeo
        title="Solutions by Industry — Where Frac Runs"
        description="Six sectors, six behavioural patterns. Consumer retail, luxury assets, financial services, supply chain, energy, and NGO. Every industry generates a different slice."
        path="/solutions"
        image="/og/solutions.png"
      />
      <div data-testid="page-solutions">
        <Hero />

        <section
          className="sticky top-[60px] z-30 border-y border-hairline bg-[rgba(2,4,11,0.72)] py-4 backdrop-blur-[24px] md:top-[72px]"
          data-testid="solutions-filter-bar"
        >
          <div className="container-frac">
            <div
              role="tablist"
              aria-label={t("filterAria")}
              className="-mx-6 flex gap-2 overflow-x-auto px-6 md:mx-0 md:flex-wrap md:overflow-visible md:px-0"
            >
              {SECTOR_SLUGS.map((slug) => (
                <Tag
                  key={slug}
                  onDark
                  selected={active === slug}
                  onClick={() => selectSector(slug)}
                  data-testid={`solutions-filter-${slug}`}
                >
                  {sectors[slug]?.label}
                </Tag>
              ))}
            </div>
          </div>
        </section>

        <section
          className="container-frac py-16 md:py-24"
          data-testid="solutions-panels"
        >
          <Stagger className="grid grid-cols-1 gap-8" gap={80}>
            {SECTOR_SLUGS.map((slug) => (
              <StaggerItem key={slug}>
                <div ref={(el) => registerPanel(slug, el)}>
                  <DetailPanel
                    slug={slug}
                    sector={sectors[slug] || {}}
                    labels={detailLabels}
                  />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </section>

        <section
          className="container-frac py-16 md:py-24"
          data-testid="solutions-adjacent"
        >
          <div className="max-w-3xl">
            <Reveal>
              <span className="eyebrow">{t("adjacent.eyebrow")}</span>
            </Reveal>
            <MaskReveal
              as="h2"
              className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
            >
              {t("adjacent.h2")}
            </MaskReveal>
            <Reveal delay={200}>
              <p className="mt-8 text-lg leading-normal text-ink-secondary">
                {t("adjacent.body")}
              </p>
            </Reveal>
          </div>
        </section>

        <section
          className="container-frac py-24 md:py-32"
          data-testid="solutions-cta"
        >
          <div className="mx-auto max-w-3xl text-center">
            <MaskReveal
              as="h2"
              className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
            >
              {t("finalCta.h2")}
            </MaskReveal>
            <Reveal delay={250}>
              <div className="mt-10">
                <Button
                  as={Link}
                  to={addLocalePrefix("/contact", locale)}
                  variant="primary-on-dark"
                  size="lg"
                  data-testid="solutions-cta-primary"
                >
                  {t("finalCta.cta")}
                </Button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
